import React, { Suspense, useEffect, useMemo, useState, useRef } from 'react';
import { useEventListeners, useOutsideClick } from 'src/hooks/utils';
import { useTranslation } from 'react-i18next';
import LoadingSkeleton from 'src/components/ui/LoadingSkeleton/LoadingSkeleton';
import Divider from 'src/components/ui/Divider/Divider';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { toast } from 'react-toastify';
import { useForm, SubmitHandler, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ProductUpdateIn, useProduct, useConfirmProduct, useSetProductToDraft } from 'src/services/productsNew';
import { getProductImageUrl } from 'src/services/products';
import equal from 'fast-deep-equal';
import { useParams } from 'react-router-dom';
import Chat from 'src/components/layouts/Chat/Chat';
import { useOverlay } from 'src/providers/OverlayProvider';
import { ProductPackaging as ProductPackagingType, useProductPackaging } from 'src/services/productPackaging';
import { QualityCheck, useQualityCheckMutations } from 'src/services/qualityCheck';
import { diffEntities } from 'src/services/utils/diffEntities';
import { useBoms } from 'src/services/boms';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import ProductImage from './ProductImage/ProductImage';
import BomComponents, { BomComponentsRef } from './BomComponents/BomComponents';
import Head from './Head/Head';
import Dimensions from './Dimensions/Dimensions';
import PrimaryInfo from './PrimaryInfo/PrimaryInfo';
import Conditions from './Conditions/Conditions';
import Tracking from './Tracking/Tracking';
import Customs from './Customs/Customs';
import TabBar from '../TabBar/TabBar';
import ProductPackaging from './ProductPackaging/ProductPackaging';

import { OverlaySectionHeadline } from '../OverlaySectionHeadline/OverlaySectionHeadline';

import {
  createProductFormSchema,
  formValuesToProductPackagings,
  formValuesToProductUpdate,
  formValuesToQualityChecks,
  ProductFormValues,
  productToFormValues,
} from './ProductFormTypes';
import { createEmptyCheck } from './Quality/QualityHelpers';
import { createEmptyPackaging } from './ProductPackaging/ProductPackagingHelpers';
import { useNavigationItems } from '../../../hooks/useNavigationItems';
import { useOverlayNavGuard } from '../../../providers/OverlayNavProvider';

const ProductInventoryLazy = React.lazy(() => import('./ProductInventory/ProductInventory'));

export interface ProductOverlayProps {
  defaultEditModeActive?: boolean;
}

const ProductOverlay: React.FC<ProductOverlayProps> = ({ defaultEditModeActive }) => {
  const mainRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { id } = useParams();
  const { onClose } = useOverlay();
  const numericId = Number(id);
  const { product, isLoading, updateProductAsync } = useProduct(numericId);
  const bomComponentsRef = useRef<BomComponentsRef>(null);

  const { createQualityCheckAsync, deleteQualityCheckAsync, updateQualityCheckAsync } = useQualityCheckMutations();
  const [activeTab, setActiveTab] = useState(0);

  const [editModeActive, setEditModeActive] = useState(defaultEditModeActive ?? false);
  const [showProductSelect, setShowProductSelect] = useState(false);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);
  const [hideHeaderActions, setHideHeaderActions] = useState(false);
  const [isConfirmProductOpen, setIsConfirmProductOpen] = useState(false);
  const [isSetToDraftOpen, setIsSetToDraftOpen] = useState(false);
  const [isEmptySetConfirmationOpen, setIsEmptySetConfirmationOpen] = useState(false);
  const { confirmProductAsync } = useConfirmProduct();
  const { setProductToDraftAsync } = useSetProductToDraft();

  const pendingNavRef = useRef<(() => void) | null>(null);

  useOverlayNavGuard(
    editModeActive
      ? (navigateFn: () => void) => {
          pendingNavRef.current = navigateFn;
          setIsCloseConfirmationOpen(true);
        }
      : null,
  );

  const { createPackagingAsync, deletePackagingAsync, updatePackagingAsync } = useProductPackaging();
  const { deleteBomMutation } = useBoms(numericId);

  const productFormSchema = useMemo(() => createProductFormSchema(), []);

  const { control, setValue, reset, handleSubmit, formState, setError, clearErrors, trigger } =
    useForm<ProductFormValues>({
      resolver: zodResolver(productFormSchema),
      defaultValues: product ? productToFormValues(product) : undefined,
      mode: 'onChange',
    });

  const { isValid, isSubmitting } = formState;

  const productId = product?.id as number | undefined;

  const editModeRef = useRef(editModeActive);

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('warehouse');
  const chatAccess = findAccessRights('chatter');

  const updateProductQualityChecks = async ({
    oldQualityChecks,
    newQualityChecks,
    productId,
  }: {
    oldQualityChecks: QualityCheck[];
    newQualityChecks: QualityCheck[];
    productId: number;
  }): Promise<boolean> => {
    const { toCreate, toUpdate, toDelete } = diffEntities({
      oldEntities: oldQualityChecks,
      newEntities: newQualityChecks,
      key: c => c.id,
      shouldCreate(entity) {
        const emptyCheckFormValues = createEmptyCheck({
          productId: entity.product_id as number,
          checkId: () => entity.id as number,
        });
        const emptyCheck = formValuesToQualityChecks([emptyCheckFormValues])[0];
        return !equal(emptyCheck, entity);
      },
    });

    const hasChanges = toCreate.length || toDelete.length || toUpdate.length;
    if (!hasChanges) return true;

    try {
      const operations = [
        ...toCreate.map(check =>
          createQualityCheckAsync({
            params: { path: { product_id: productId } },
            body: check,
          }),
        ),
        ...toDelete.map(check =>
          deleteQualityCheckAsync({
            params: { path: { quality_check_id: check.id! } },
          }),
        ),
        ...toUpdate.map(check =>
          updateQualityCheckAsync({
            params: { path: { quality_check_id: check.id! } },
            body: check,
          }),
        ),
      ];

      await Promise.all(operations);
      return true;
    } catch (error) {
      console.error(error);
      toast.error(t('errors.quality_checks_update_failed'));
      return false;
    }
  };

  const updateProductPackagings = async ({
    oldProductPackagings,
    newProductPackagings,
    productId,
  }: {
    oldProductPackagings: ProductPackagingType[];
    newProductPackagings: ProductPackagingType[];
    productId: number;
  }): Promise<boolean> => {
    const { toCreate, toUpdate, toDelete } = diffEntities({
      oldEntities: oldProductPackagings,
      newEntities: newProductPackagings,
      key: c => c.id,
      shouldCreate(entity) {
        const emptyPackaging = createEmptyPackaging(() => entity.id as number);
        return !equal(emptyPackaging, entity);
      },
    });

    const hasChanges = toCreate.length || toDelete.length || toUpdate.length;
    if (!hasChanges) return true;

    try {
      const operations = [
        ...toCreate.map(packaging =>
          createPackagingAsync({
            params: { path: { product_id: productId } },
            body: packaging,
          }),
        ),
        ...toDelete.map(packaging =>
          deletePackagingAsync({
            params: { path: { packaging_id: packaging.id! } },
          }),
        ),
        ...toUpdate.map(packaging =>
          updatePackagingAsync({
            params: { path: { packaging_id: packaging.id! } },
            body: packaging,
          }),
        ),
      ];

      await Promise.all(operations);
      return true;
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.detail || e?.response?.data?.detail || e?.message || t('errors.product_packagings_update_failed');
      toast.error(
        errorMessage?.toLowerCase()?.includes('unique-constraint') ? t('errors.duplicate_barcode') : errorMessage,
        { autoClose: 5000 },
      );
      return false;
    }
  };

  const onSubmit: SubmitHandler<ProductFormValues> = async data => {
    if (typeof productId !== 'number' || !product) {
      throw new Error(`Product ID (${productId}) not valid or product is not defined`);
    }

    const productForm = productToFormValues(product);
    const productData = formValuesToProductUpdate(data, productForm);
    const switchingToDigital = data.isNonShippable && !product.is_non_shippable;

    try {
      if (switchingToDigital) {
        const existingPackagings = formValuesToProductPackagings(productForm.productPackagings);
        const existingBoms = product.bom_components ?? [];

        await Promise.all([
          ...(existingPackagings.length > 0
            ? [
                updateProductPackagings({
                  oldProductPackagings: existingPackagings,
                  newProductPackagings: [],
                  productId,
                }),
              ]
            : []),
          ...existingBoms
            .filter(bom => bom.id != null)
            .map(bom => deleteBomMutation.mutateAsync({ params: { path: { bom_id: bom.id! } } })),
        ]);
      }

      if (activeTab === 1 && bomComponentsRef.current) {
        await bomComponentsRef.current.saveChanges();
      }

      await updateProductAsync({
        params: { path: { product_id: productId } },
        body: productData as ProductUpdateIn,
      });
      setEditModeActive(false);

      const [qualitySuccess, packagingSuccess] = await Promise.all([
        updateProductQualityChecks({
          oldQualityChecks: formValuesToQualityChecks(productForm.qualityChecks),
          newQualityChecks: formValuesToQualityChecks(data.qualityChecks),
          productId,
        }),
        switchingToDigital
          ? true
          : updateProductPackagings({
              oldProductPackagings: formValuesToProductPackagings(productForm.productPackagings),
              newProductPackagings: formValuesToProductPackagings(data.productPackagings),
              productId,
            }),
      ]);

      if (qualitySuccess && packagingSuccess) {
        toast.success(t('success.product_update_successful'));
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage = e?.detail || e?.response?.data?.detail || e?.message || t('errors.update_product');
      toast.error(errorMessage, { autoClose: 5000 });
    }
  };

  const isNonShippable = useWatch({ control, name: 'isNonShippable' });
  const isProductSet = useWatch({ control, name: 'isProductSet' });

  const storableProduct = !isProductSet && !isNonShippable;

  const handleEmptySetConfirm = (confirmed: boolean) => {
    setIsEmptySetConfirmationOpen(false);
    if (confirmed) {
      // the dialog promises that an empty set is saved as a stockable
      // product — flip the type so the empty BoM is cleaned up on submit
      setValue('isProductSet', false);
      handleSubmit(onSubmit)();
    }
  };

  const handleHeaderAction = (action: 'cancel' | 'save' | 'close' | 'confirm' | 'draft') => {
    if (action === 'confirm') {
      setIsConfirmProductOpen(true);
    } else if (action === 'draft') {
      setIsSetToDraftOpen(true);
    } else if (action === 'cancel') {
      setIsCancelConfirmationOpen(true);
    } else if (action === 'save') {
      const bomLineCount = bomComponentsRef.current
        ? bomComponentsRef.current.selectedProducts.length
        : product?.bom_components?.length ?? 0;
      if (isProductSet && bomLineCount === 0) {
        setIsEmptySetConfirmationOpen(true);
      } else {
        handleSubmit(onSubmit)();
      }
    } else {
      if (editModeActive) {
        setIsCloseConfirmationOpen(true);
      } else {
        onClose();
      }
    }
  };

  const handleConfirmProduct = async (confirmed: boolean) => {
    setIsConfirmProductOpen(false);
    if (confirmed && typeof productId === 'number') {
      try {
        await confirmProductAsync({
          params: { path: { product_id: productId } },
        });
        toast.success(t('success.product_update_successful'));
      } catch (e: any) {
        console.error(e);
        const errorMessage = e?.detail || e?.response?.data?.detail || e?.message || t('errors.update_product');
        toast.error(errorMessage, { autoClose: 5000 });
      }
    }
  };

  const handleSetToDraft = async (confirmed: boolean) => {
    setIsSetToDraftOpen(false);
    if (confirmed && typeof productId === 'number') {
      try {
        await setProductToDraftAsync({
          params: { path: { product_id: productId } },
        });
        toast.success(t('success.product_update_successful'));
      } catch (e: any) {
        console.error(e);
        const errorMessage = e?.detail || e?.response?.data?.detail || e?.message || t('errors.update_product');
        toast.error(errorMessage, { autoClose: 5000 });
      }
    }
  };

  const handleConfirmClose = (confirmed: boolean) => {
    if (confirmed) {
      if (pendingNavRef.current) {
        setEditModeActive(false);
        pendingNavRef.current();
        pendingNavRef.current = null;
      } else if (isCloseConfirmationOpen) {
        onClose();
      } else {
        if (showProductSelect) {
          setShowProductSelect(false);
          return;
        }
        setEditModeActive(false);
      }
    } else {
      pendingNavRef.current = null;
    }

    setIsCancelConfirmationOpen(false);
    setIsCloseConfirmationOpen(false);
  };

  useEffect(() => {
    setEditModeActive(defaultEditModeActive ?? false);
    setActiveTab(0);
    setShowProductSelect(false);
    setIsCloseConfirmationOpen(false);
    setIsCancelConfirmationOpen(false);
    setHideHeaderActions(false);
    setIsConfirmProductOpen(false);
    setIsSetToDraftOpen(false);
  }, [id]);

  useEffect(() => {
    editModeRef.current = editModeActive;
  }, [editModeActive]);

  useOutsideClick(mainRef, e => {
    const event = e as MouseEvent;
    if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
      if (editModeRef.current) {
        setIsCloseConfirmationOpen(true);
      } else {
        onClose();
      }
    }
  });

  useEventListeners([{ event: 'keydown' }], e => {
    const event = e as KeyboardEvent;
    if (event.key === 'Escape') {
      if (editModeRef.current) {
        setIsCloseConfirmationOpen(true);
      } else {
        onClose();
      }
    }
  });

  useEffect(() => {
    if (product) {
      reset(productToFormValues(product));
    }
  }, [editModeActive]);

  useEffect(() => {
    if (product) {
      reset(productToFormValues(product));
    }
  }, [product, reset]);

  const productStatus = useWatch({ control, name: 'productStatus' });

  const disabled = product && !!product?.id && !editModeActive;
  const lockedUnlessDraft = product && !!product?.id && productStatus !== 'Draft';

  // Reset quality checks on disable
  const qualityCheckEnabled = useWatch({ control, name: 'qualityCheckEnabled' });
  useEffect(() => {
    if (!qualityCheckEnabled && product) {
      setValue('qualityChecks', productToFormValues(product).qualityChecks);
    }
  }, [qualityCheckEnabled]);

  const tabs = [
    {
      label: t('general.product_details'),
      onClick: () => setActiveTab(0),
      visible: () => true,
    },
    {
      label: t('general.product_set'),
      onClick: () => setActiveTab(1),
      visible: () => isProductSet,
    },
    {
      label: t('general.product_packaging'),
      onClick: () => setActiveTab(3),
      visible: () => {
        return storableProduct;
      },
    },
    {
      label: t('product.inventory_details'),
      onClick: () => setActiveTab(4),
      visible: () => {
        return !isNonShippable;
      },
    },
  ];

  // Memoize tabsToShow so reference changes only when relevant dependencies change
  const tabsToShow = useMemo(
    () => tabs.filter(t => t.visible()),
    [isProductSet, isNonShippable, product, storableProduct],
  );

  // Reset activeTab if the current tab is not visible whenever tabsToShow changes
  useEffect(() => {
    if (!tabsToShow[activeTab]) {
      setActiveTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsToShow]);

  const productName = useWatch({ control, name: 'name' });
  const productVolume = product?.volume as number | undefined;

  const weight = useWatch({ control, name: 'weight' });
  useEffect(() => {
    trigger('nettoWeight');
  }, [weight]);

  useEffect(() => {
    if (editModeActive) return;
    const shouldBeProductSet =
      product &&
      !product.is_non_shippable &&
      product?.bom_components &&
      product.bom_components.length > 0 &&
      !product.product_packagings?.length;

    setValue('isProductSet', !!shouldBeProductSet);
  }, [product, setValue, editModeActive]);

  useEffect(() => {
    const handler = () => {
      if (activeTab === 1 && bomComponentsRef.current) {
        setHideHeaderActions(bomComponentsRef.current.isSelectingProducts);
      } else {
        setHideHeaderActions(false);
      }
    };

    handler();
    const interval = setInterval(handler, 100);
    return () => clearInterval(interval);
  }, [activeTab, bomComponentsRef.current?.isSelectingProducts]);

  const isSaveDisabled = useMemo(() => {
    if (isSubmitting) return true;
    if (!isValid) return true;
    if (Object.keys(formState.errors).length > 0) return true;
    return false;
  }, [
    isSubmitting,
    isValid,
    isProductSet,
    isNonShippable,
    storableProduct,
    formState,
    bomComponentsRef.current?.selectedProducts,
  ]);

  useEffect(() => {
    if (editModeActive) {
      trigger(['weight', 'nettoWeight']);
    }
  }, [editModeActive, trigger]);

  return (
    <div
      ref={mainRef}
      data-overlay-handles-outside="true"
      data-overlay-handles-escape="true"
      data-overlay-type="product"
      style={{ height: '100%', overflow: 'auto' }}>
      <Head
        disabled={isSaveDisabled}
        product={product ?? null}
        edit={editModeActive}
        setEdit={setEditModeActive}
        onHandleAction={handleHeaderAction}
        hideActions={hideHeaderActions || !accessRights?.read_write}
        showConfirm={!editModeActive && product?.product_status === 'Draft'}
        showSetToDraft={!editModeActive && product?.product_status === 'Confirmed'}
        canSetToDraft={!!product?.can_set_to_draft}
      />
      {isLoading && <LoadingSkeleton />}
      <ContainerBody>
        {!isLoading && !showProductSelect && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <h3 className="typo-title-36 text-full mb-40">{productId ? productName : t('general.create_product')}</h3>
            {/* <ProductImage onImageChange={f => setLogoFile(f)} disabled={!editModeActive} product_id={productId} /> */}
            <ProductImage
              control={control}
              name="image"
              product_id={productId as number | undefined}
              productImage={productId ? getProductImageUrl(productId) : ''}
              disabled={!editModeActive}
            />
            {/* Force TabBar to remount when tabsToShow changes by using a key */}
            <TabBar
              className="mt-40"
              tabs={tabsToShow}
              defaultIndex={0}
              key={tabsToShow.map(tab => tab.label).join('-')}
            />
            <div className="mt-40">
              {activeTab === 0 && (
                <>
                  <OverlaySectionHeadline as="h4">{t('general.product_details')}</OverlaySectionHeadline>
                  <Conditions
                    lockedUnlessDraft={lockedUnlessDraft}
                    disabled={!editModeActive}
                    control={control}
                    isEditMode
                  />
                  <PrimaryInfo
                    control={control}
                    errors={formState.errors}
                    setError={setError}
                    clearErrors={clearErrors}
                    editDisabled={!editModeActive}
                    lockedUnlessDraft={lockedUnlessDraft}
                    isSetProduct={isProductSet ?? false}
                  />
                </>
              )}
              {activeTab === 1 && productId && (
                // TODO bom validation
                <BomComponents
                  ref={bomComponentsRef}
                  hasProductSetEnabled={isProductSet}
                  productId={productId}
                  editable={!disabled}
                />
              )}
              {activeTab === 0 && (
                <>
                  <Divider className="my-40" />
                  {!(isNonShippable || isProductSet) && (
                    <>
                      <Dimensions
                        editModeActive={editModeActive}
                        control={control}
                        errors={formState.errors}
                        volume={productVolume ?? null}
                      />
                      <Divider className="my-40" />
                      <Customs control={control} errors={formState.errors} editModeActive={editModeActive} />
                      <Divider className="my-40" />
                      {/* {productId && ( */}
                      {/*  <> */}
                      {/*    <Quality */}
                      {/*      setValue={setValue} */}
                      {/*      productId={productId} */}
                      {/*      control={control} */}
                      {/*      errors={formState.errors} */}
                      {/*      editModeActive={editModeActive} */}
                      {/*    /> */}
                      {/*    <Divider className="my-40" /> */}
                      {/*  </> */}
                      {/* )} */}
                    </>
                  )}
                  {/* {!isNonShippable && ( */}
                  {/*  <> */}
                  {/*    <Tags */}
                  {/*      initialTags={product?.tags || []} */}
                  {/*      editModeActive={editModeActive} */}
                  {/*      setValue={setValue} */}
                  {/*      control={control} */}
                  {/*      errors={formState.errors} */}
                  {/*    /> */}
                  {/*    <Divider className="my-40" /> */}
                  {/*  </> */}
                  {/* )} */}
                  {!(isNonShippable || isProductSet || product?.product_status !== 'Draft') && (
                    <>
                      <Tracking control={control} editModeActive={editModeActive} />
                      <Divider className="my-40" />
                    </>
                  )}

                  {/* {!isNonShippable && !isProductSet && ( */}
                  {/*  <> */}
                  {/*    <ProductAttachments */}
                  {/*      editModeActive={editModeActive} */}
                  {/*      attachments={attachments} */}
                  {/*      setValue={setValue} */}
                  {/*      errors={formState.errors} */}
                  {/*    /> */}
                  {/*    <Divider className="my-40" /> */}
                  {/*  </> */}
                  {/* )} */}
                  {typeof productId === 'number' &&
                    !Number.isNaN(productId) &&
                    (chatAccess?.read || chatAccess?.read_write) && (
                      <Chat
                        model="product.product"
                        res_id={productId}
                        withCreateEditMessage={accessRights?.read_write}
                      />
                    )}
                </>
              )}
              {activeTab === 3 && productId && (
                <ProductPackaging
                  control={control}
                  errors={formState.errors}
                  setValue={setValue}
                  setError={setError}
                  clearErrors={clearErrors}
                  editModeActive={editModeActive}
                />
              )}
              {activeTab === 4 && productId && (
                <Suspense>
                  <ProductInventoryLazy productId={productId} />
                </Suspense>
              )}
            </div>
          </form>
        )}
        <ConfirmationModal
          isOpen={isCloseConfirmationOpen || isCancelConfirmationOpen}
          title={isCloseConfirmationOpen ? t('product_inventory.close_overlay') : t('product_inventory.exit_edit_mode')}
          description={
            isCloseConfirmationOpen
              ? t('product_inventory.close_overlay_text')
              : t('product_inventory.exit_edit_mode_text')
          }
          onClose={handleConfirmClose}
        />
        <ConfirmationModal
          isOpen={isConfirmProductOpen}
          title={t('product_inventory.confirm_product_title')}
          description={t('product_inventory.confirm_product_text')}
          onClose={handleConfirmProduct}
        />
        <ConfirmationModal
          isOpen={isSetToDraftOpen}
          title={t('product_inventory.set_to_draft_title')}
          description={t('product_inventory.set_to_draft_text')}
          onClose={handleSetToDraft}
        />
        <ConfirmationModal
          isOpen={isEmptySetConfirmationOpen}
          title={t('product_inventory.empty_set_title')}
          description={t('product_inventory.empty_set_description')}
          onClose={handleEmptySetConfirm}
        />
      </ContainerBody>
    </div>
  );
};

export default ProductOverlay;
